import { Eye, Lock, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { EmailTemplateList } from '@/types/template';
import { previewEmailTemplate } from '@/services/templateService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  EmailTemplateCategory,
  EMAIL_CATEGORY_LABELS,
  getCategoryBadgeColor,
} from '../constants';

interface EmailTemplatePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateList | null;
}

export function EmailTemplatePreviewDialog({
  open,
  onOpenChange,
  template,
}: EmailTemplatePreviewDialogProps) {
  const { data: previewData, isLoading } = useQuery({
    queryKey: ['templates', 'email-preview', template?.id],
    queryFn: () => previewEmailTemplate(template!.id),
    enabled: open && !!template,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Preview: {template?.name}
            {template?.source === 'SYSTEM' && (
              <Badge
                variant="outline"
                className="border-border text-muted-foreground gap-1 text-xs"
              >
                <Lock className="h-3 w-3" />
                System
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {template?.source === 'SYSTEM'
              ? 'This is the built-in system template. It cannot be edited but can be overridden by creating a custom template and setting it as active.'
              : 'Preview of the email template rendered with sample data.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Template metadata */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Category: </span>
              <Badge
                className={getCategoryBadgeColor(template?.template_type || '')}
              >
                {
                  EMAIL_CATEGORY_LABELS[
                    (template?.template_type ||
                      'general') as EmailTemplateCategory
                  ]
                }
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Fields: </span>
              <span className="font-medium">{template?.field_count}</span>
            </div>
          </div>

          {/* Subject preview */}
          {template && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-3 py-2 border-b text-xs text-muted-foreground font-medium">
                Subject
              </div>
              <div className="p-3 bg-background text-sm font-medium">
                {isLoading || !previewData ? (
                  <span className="text-muted-foreground">Loading...</span>
                ) : (
                  previewData.subject
                )}
              </div>
            </div>
          )}

          {/* Rendered body preview */}
          {template && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-3 py-2 border-b text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Rendered Preview with sample data
              </div>
              <div className="p-4 bg-background min-h-[300px]">
                {isLoading || !previewData ? (
                  <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <iframe
                    srcDoc={previewData.html}
                    className="w-full min-h-[400px] border-0"
                    title="Email template preview"
                    sandbox=""
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
